import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useState } from "react";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Flex from "../../../../../../../components/Flex";
import LabeledSwitch from "../../../../../../../components/LabeledSwitch";
import CustomTabs from "../../../../../../../components/app/CustomTabs";
import { ChartActionStrip } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useSelectedPatient from "@/hooks/local-storage/use-selected-patient";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useSyncScriptsureAllergies } from "../../../../../../../queries/script-sure/use-sync-scriptsure-allergies";
import {
  getXtenantId,
  roleFilter,
  showToastOnApiCall,
} from "../../../../../../../utils/utils";
import UpsertAllergiesDrawer from "../allergy/UpsertAllergiesDrawer";
import UpsertCarePlanDrawer from "../care-plan/UpsertCarePlanDrawer";
import UpsertDiagnosesDrawer from "../diagnoses/UpsertDiagnosisDrawer";
import UpsertMedicationsDrawer from "../medication/UpsertMedicationsDrawer";
import UpsertVaccineDrawer from "../vaccines/UpsertVaccineDrawer";
import UpsertVitalFormDrawerNew from "../vitals/UpsertVitalFormDrawerNew";
import AllergiesList from "./components/AllergiesList";
import CarePlanList from "./components/CarePlanList";
import { CLINICAL_DETAILS_TABS_DATA } from "./components/ClinicalLayout-constant";
import DiagnosisList from "./components/DiagnosisList";
import MedicationsList from "./components/MedicationsList";
import VaccinesList from "./components/VaccinesList";
import VitalsList from "./components/VitalsList";
// import UpsertScriptsureAllergiesDrawer from "../allergy/UpsertScriptsureAllergiesDrawer";

// const isProduction = import.meta.env.VITE_ENV_NAME === "prod";

const ClinicalLayoutNew = () => {
  const [
    diagnosesDisclosure,
    allergiesDisclosure,
    medicationsDisclosure,
    vaccinesDisclosure,
    careplanDisclosure,
    vitalsDisclosure,
  ] = useReduceDisclosure(6);

  const { patientId } = useParams() as { patientId: string };
  const [activeButton, setActiveButton] = useState<string>("");
  const { hasView: hasClinicalViewAccess } = useRoleController("Clinical");
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPath = window.location.pathname;
  const isVisitNoteTab = currentPath.includes("/visit-note");

  const prefillActionSection = () => {
    // Inside an ongoing encounter the Clinicals panel is driven by the
    // clinicalSection query param, set when "View All" is clicked from the
    // Quick Health Records sidebar. Default to Allergies ("").
    if (isVisitNoteTab) {
      setActiveButton(searchParams.get("clinicalSection") ?? "");
      return;
    }

    const section = location.pathname.split("/").pop() || "";
    const activeTab = section === "clinical-details" ? "" : section;
    setActiveButton(activeTab);
  };

  // The effect keys off the raw pathname string rather than a reactive value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(prefillActionSection, [location.pathname, searchParams]);

  const { mutateAsync: syncScriptsureAllergies } = useSyncScriptsureAllergies();

  /**
   * Allergies can only be pulled from ScriptSure once the patient is
   * registered there, so the Sync action stays hidden until then instead of
   * failing with a generic error.
   */
  const [selectedPatient] = useSelectedPatient();
  const isScriptSureConnected = !!selectedPatient?.isScriptSure;

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleSyncToScriptSure = () => {
    showToastOnApiCall({
      apiCall: syncScriptsureAllergies({
        patientId: patientId,
        xTENANTID: getXtenantId() ?? "",
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({ queryKey: ["patient-allergy-list"] });
      },
    });
  };

  const { hasUpdate: hasUpdateClinical } = useRoleController("Clinical");
  const [showArchived, setShowArchived] = useState<boolean>(false);

  const endContentMap: Record<string, ReactNode> = {
    "": (
      <>
        {/* // we are keeping the sync button for allergies as per the request from
        clinical team, but hiding it behind a feature flag for now until we are
        ready to use it in prod. (ECR-5039) */}
        {/* {isProduction && ( */}
        {isScriptSureConnected && (
          <ChartActionStrip.Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncToScriptSure}
          >
            Sync
          </ChartActionStrip.Button>
        )}
        {/* )} */}
        <ChartActionStrip.Button
          startIcon={<AddIcon />}
          onClick={() => allergiesDisclosure.open()}
        >
          Add Allergy
        </ChartActionStrip.Button>
      </>
    ),
    diagnosis: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => diagnosesDisclosure.open()}
      >
        Add Diagnosis
      </ChartActionStrip.Button>
    ),
    medications: (
      <Flex alignItems="center" gap={1}>
        <LabeledSwitch
          label="Show Past Medication"
          checked={searchParams.get("showPastMedications") === "true"}
          onChange={(e) => {
            setSearchParams({
              showPastMedications: e.target.checked ? "true" : "false",
            });
          }}
        />
        <ChartActionStrip.Button
          startIcon={<AddIcon />}
          onClick={() => medicationsDisclosure.open()}
        >
          Add Medication
        </ChartActionStrip.Button>
      </Flex>
    ),
    vaccines: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => vaccinesDisclosure.open()}
      >
        Add Vaccine
      </ChartActionStrip.Button>
    ),
    vitals: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => vitalsDisclosure.open()}
      >
        Add Vitals
      </ChartActionStrip.Button>
    ),
    care_plan: (
      <Flex alignItems="center" gap={1}>
        <LabeledSwitch
          label="Show Archived Care Plans"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
        <ChartActionStrip.Button
          startIcon={<AddIcon />}
          onClick={() => careplanDisclosure.open()}
        >
          Add Care Plan
        </ChartActionStrip.Button>
      </Flex>
    ),
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UpsertAllergiesDrawer disclosureHost={allergiesDisclosure} />
      <UpsertDiagnosesDrawer disclosureHost={diagnosesDisclosure} />
      <UpsertMedicationsDrawer disclosureHost={medicationsDisclosure} />
      <UpsertVaccineDrawer disclosureHost={vaccinesDisclosure} />
      <UpsertCarePlanDrawer
        disclosureHost={careplanDisclosure}
        curFormState="add"
      />
      <UpsertVitalFormDrawerNew disclosureHost={vitalsDisclosure} />

      <ChartActionStrip
        startContent={
          <CustomTabs
            tabsData={roleFilter({
              list: CLINICAL_DETAILS_TABS_DATA,
              compWith: {
                vaccines: hasClinicalViewAccess,
                vitals: hasClinicalViewAccess,
                care_plan: hasClinicalViewAccess,
                diagnosis: hasClinicalViewAccess,
                medications: hasClinicalViewAccess,
                allergies: hasClinicalViewAccess,
              },
            })}
            value={activeButton}
            onChange={(v) => {
              setActiveButton(v);
              if (isVisitNoteTab) {
                setSearchParams(
                  (prev) => {
                    prev.set("clinicalSection", v);
                    return prev;
                  },
                  { replace: true }
                );
              } else {
                navigate(v);
              }
            }}
          />
        }
        endContent={
          hasUpdateClinical && !isVisitNoteTab
            ? endContentMap[activeButton]
            : undefined
        }
      />

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {isVisitNoteTab ? (
          ClinicalDetailsMapper[activeButton]
        ) : (
          <Outlet context={{ showArchived }} />
        )}
      </Box>
    </Box>
  );
};

export default ClinicalLayoutNew;

const ClinicalDetailsMapper: Record<string, ReactNode> = {
  "": <AllergiesList isInsideVisitNote={true} />,
  diagnosis: <DiagnosisList isInsideVisitNote={true} />,
  medications: <MedicationsList isInsideVisitNote={true} />,
  vaccines: <VaccinesList isInsideVisitNote={true} />,
  vitals: <VitalsList isInsideVisitNote={true} />,
  care_plan: <CarePlanList isInsideVisitNote={true} />,
};
